import React, { useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from 'react-query';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Button } from '@vimily/bonjoro-ui-components';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { invoiceApi } from '../../api';
import cx from 'classnames';
import FlashContext from '~/App/Components/Flash/Context';
import { ErrorMessage } from '@hookform/error-message';
import { useMemo } from 'react';
import { useHistory } from 'react-router';
import analytics from '~/utils/Analytics';
import { BILLING_UPDATE_INFO_SAVE } from '~/generated/AnalyticsTypes';
import { queryKeys } from '../../../NewBilling/Hooks/useInvoiceInfo';

const FieldErrorMessage = styled.div`
  color: #d0577d;
  font-size: 12px;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 405px;
`;

const Title = styled.h2`
  font-size: 26px;
  color: #323232;
  margin-bottom: 30px;
  font-weight: bold;
`;

const Label = styled.label`
  color: #858585;
  font-weight: normal;
  margin-bottom: 12px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const TextArea = styled.textarea`
  resize: none;
`;

export const App = () => {
  const api = useMemo(() => invoiceApi(), []);
  const history = useHistory();

  const { data } = useQuery('invoice-profile', () => api.get(''), {
    refetchOnWindowFocus: false,
  });
  const { register, handleSubmit, setValue, errors } = useForm();
  const queryClient = useQueryClient();

  const { createFlash } = useContext(FlashContext);

  useEffect(() => {
    if (data) {
      setValue('billing_name', data.name);
      setValue('billing_address', data.address);
      setValue('billing_tax_id', data.tax_id);
    }
  }, [data]);

  const { mutateAsync: updateMutation, isLoading: isUpdating } = useMutation(
    (data) => api.put('', data),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries('invoice-profile');
        queryClient.invalidateQueries(queryKeys.detail);

        createFlash({
          id: 'update-invoice-profile',
          title: 'Success',
          message: 'Invoice updated successfully',
        });

        await analytics.track(BILLING_UPDATE_INFO_SAVE);

        history.push('/billing');
      },
    }
  );

  const onSubmit = async (data) => await updateMutation(data);

  const validateEmptyString = (value: string) =>
    value.trim() == '' ? 'Please enter a value' : true;

  return (
    <Wrapper>
      <Title>Update invoice details</Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label>Billing name</Label>
          <input
            type='text'
            name='billing_name'
            ref={register({
              required: 'Field is required',
              validate: validateEmptyString,
            })}
            className={cx('form-control', {
              'is-invalid': errors && errors.billing_name,
            })}
          />
          <ErrorMessage
            name='billing_name'
            errors={errors}
            as={FieldErrorMessage}
          />
        </FormGroup>
        <FormGroup>
          <Label>Tax Id</Label>
          <input
            type='text'
            name='billing_tax_id'
            ref={register({
              required: 'Field is required',
              validate: validateEmptyString,
            })}
            className={cx('form-control', {
              'is-invalid': errors && errors.billing_tax_id,
            })}
          />
          <ErrorMessage
            name='billing_tax_id'
            errors={errors}
            as={FieldErrorMessage}
          />
        </FormGroup>
        <FormGroup>
          <Label>Billing Address</Label>
          <TextArea
            rows={4}
            cols={50}
            name='billing_address'
            ref={register({
              required: 'Field is required',
              validate: validateEmptyString,
            })}
            className={cx('form-control', {
              'is-invalid': errors && errors.billing_address,
            })}
          />
          <ErrorMessage
            name='billing_address'
            errors={errors}
            as={FieldErrorMessage}
          />
        </FormGroup>
        <div className='flex items-center flex-center justify-end'>
          <Link to='/billing'>
            <Button variant='secondary' className='mr-4'>
              Cancel
            </Button>
          </Link>
          <Button disabled={isUpdating}>Save</Button>
        </div>
      </form>
    </Wrapper>
  );
};

const Edit = () => {
  const targetElement = document.getElementById('bonjoro-app') as HTMLElement;
  return createPortal(<App />, targetElement);
};

export default Edit;
